import React, { useState } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import { machines } from '../../data/machines';
import machineBanner from '../../assets/another page banner/machine banner.jpg';
import './Machines.css';

// Inspection facility images
import imgBoreGauge     from '../../assets/inspection facilities bg remover/Bore_Gauges-removebg-preview.png';
import imgInsideTubular from '../../assets/inspection facilities bg remover/Inside_Tubular_Micrometer-removebg-preview.png';
import imgOutsideMic    from '../../assets/inspection facilities bg remover/Outside_Micrometer-removebg-preview.png';
import imgPiTapes       from '../../assets/inspection facilities bg remover/Pi_Tapes-removebg-preview.png';
import imgSurfaceTable  from '../../assets/inspection facilities bg remover/Surface_Table__2_Nos_-removebg-preview.png';
import imgTubularXL     from '../../assets/inspection facilities bg remover/Tubular_Micrometer_XL-removebg-preview.png';
import imgUnimaster     from '../../assets/inspection facilities bg remover/Uni-master__TESA_-removebg-preview.png';
import imgVernier       from '../../assets/inspection facilities bg remover/Vernier_Calipers-removebg-preview.png';

const ACCENTS = ['#4F46E5','#7C3AED','#E8192C','#10B981','#F59E0B','#EA580C','#0EA5E9','#F43F5E'];

const INSPECTION = [
  { img: imgUnimaster,     label: 'Uni-master (TESA)',          spec: '100–3500 mm',    clr: '#4F46E5' },
  { img: imgInsideTubular, label: 'Inside Tubular Micrometer', spec: '100–4000 mm',    clr: '#E8192C' },
  { img: imgSurfaceTable,  label: 'Surface Table (2 Nos)',      spec: '1000 × 1000 mm', clr: '#10B981' },
  { img: imgTubularXL,     label: 'Tubular Micrometer XL',     spec: '1000–5000 mm',   clr: '#F59E0B' },
  { img: imgOutsideMic,    label: 'Outside Micrometer',        spec: '0–1100 mm',      clr: '#7C3AED' },
  { img: imgVernier,       label: 'Vernier Calipers',          spec: '0–1000 mm',      clr: '#0EA5E9' },
  { img: imgPiTapes,       label: 'Pi Tapes',                  spec: 'Up to 3050 mm',  clr: '#EA580C' },
  { img: imgBoreGauge,     label: 'Bore Gauges',               spec: 'Various ranges', clr: '#F43F5E' },
];

// ── MACHINE DETAIL PANEL ──
function MachineDetailPanel({ machine, accent }) {
  if (!machine) {
    return (
      <div className="mdet-empty">
        <div className="mdet-empty__icon">⚙️</div>
        <p className="mdet-empty__text">Select a machine to view full specifications</p>
      </div>
    );
  }

  return (
    <div className="mdet" key={machine.id} style={{ '--m-accent': accent }}>
      <div className="mdet__img-wrap">
        <img
          src={machine.image}
          alt={machine.name}
          className="mdet__img"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80'; }}
        />
        <div className="mdet__img-overlay" />
        <span className="mdet__unit-badge">Unit {machine.unit} — Thirumudivakkam</span>
      </div>

      <div className="mdet__body">
        <span className="mdet__make">{machine.make}</span>
        <h2 className="mdet__name">{machine.name}</h2>
        <p className="mdet__type">{machine.type}</p>
        <p className="mdet__highlight">{machine.highlight}</p>

        <div className="mdet__divider" />

        <h4 className="mdet__specs-title">Technical Specifications</h4>
        <div className="mdet__specs-list">
          {Object.entries(machine.specs).map(([k, v]) => (
            <div key={k} className="mdet__spec-row">
              <span className="mdet__spec-key">{k}</span>
              <span className="mdet__spec-val">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── MACHINE CARD ──
function MachineCard({ machine, isActive, onClick, accent, mobileDetail }) {
  const [tab, setTab] = useState('spec');

  const isFlagship = machine.name === 'BERTHIEZ VTL 8.2m';
  const isKolb     = machine.name.includes('KOLB');
  const isCrane    = machine.type && machine.type.toLowerCase().includes('crane');
  const showBadge  = isFlagship || isKolb || isCrane;
  const badgeLabel = isFlagship ? '🏆 FLAGSHIP — BERTHIEZ VTL 8.2m'
                   : isKolb    ? '⚙ KOLB GANTRY — Germany'
                   : isCrane   ? '🏗 EOT CRANE'
                   : '';

  return (
    <div
      className={`mcard ${isActive ? 'mcard--active' : ''} ${isFlagship ? 'mcard--flagship-card' : ''}`}
      style={{ '--m-accent': accent }}
    >
      {showBadge && (
        <div className={`mcard__flagship ${isFlagship ? 'mcard__flagship--hero' : ''}`}>
          {badgeLabel}
        </div>
      )}
      {/* Top row: image + name */}
      <div className="mcard__top" onClick={onClick}>
        <div className="mcard__img-wrap">
          <img
            src={machine.image}
            alt={machine.name}
            className="mcard__img"
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80'; }}
          />
        </div>
        <div className="mcard__meta">
          <span className="mcard__unit">Unit {machine.unit} — Thirumudivakkam</span>
          <h3 className="mcard__name">{machine.name}</h3>
          <span className="mcard__make">{machine.make}</span>
          <p className="mcard__type">{machine.type}</p>
        </div>
        <div className="mcard__arrow">{isActive ? '▴' : '▾'}</div>
      </div>

      {/* Tab buttons */}
      <div className="mcard__tabs">
        <button
          className={`mcard__tab ${tab === 'spec' ? 'mcard__tab--active' : ''}`}
          onClick={() => setTab('spec')}
        >
          Specifications
        </button>
        <button
          className={`mcard__tab ${tab === 'details' ? 'mcard__tab--active' : ''}`}
          onClick={() => { setTab('details'); onClick(); }}
        >
          Details →
        </button>
      </div>

      {tab === 'spec' && (
        <div className="mcard__spec-preview">
          {Object.entries(machine.specs).slice(0, 4).map(([k, v]) => (
            <div key={k} className="mcard__spec-row">
              <span>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </div>
      )}

      {tab === 'details' && (
        <div className="mcard__detail-preview">
          <p className="mcard__highlight">{machine.highlight}</p>
          <button className="btn btn-indigo mcard__view-btn" onClick={onClick}>
            View Full Specs ↓
          </button>
        </div>
      )}

      {/* Mobile inline detail — renders directly below the card, not in a sidebar */}
      {isActive && mobileDetail && (
        <div className="mcard__mobile-detail">
          <MachineDetailPanel machine={machine} accent={accent} />
        </div>
      )}
    </div>
  );
}

export default function Machines() {
  const [activeUnit,  setActiveUnit]  = useState(0);
  const [selectedId,  setSelectedId]  = useState(null);
  const gridRef = useScrollReveal();

  // High-load / Unit II machines first, then Unit I
  const sortedMachines = [...machines].sort((a, b) => {
    if (a.unit !== b.unit) return b.unit - a.unit; // Unit 2 first (larger load)
    return a.id - b.id;
  });

  const filtered = activeUnit ? sortedMachines.filter(m => m.unit === activeUnit) : sortedMachines;
  const selectedMachine = machines.find(m => m.id === selectedId) || null;
  const selectedAccent  = selectedMachine ? (ACCENTS[selectedMachine.id - 1] || '#4F46E5') : '#4F46E5';

  const handleCardClick = (id) => {
    setSelectedId(prev => prev === id ? null : id);
  };

  return (
    <main className="machines-page">

      {/* ── HERO — with banner image ── */}
      <section className="page-hero">
        <div className="page-hero__bg">
          <img src={machineBanner} alt="Machines" className="page-hero__bg-img" />
          <div className="page-hero__bg-overlay" />
        </div>
        <div className="container page-hero__content">
          <p className="page-hero__tag">Our Facility</p>
          <h1 className="page-hero__title display">Machines</h1>
          <p className="page-hero__sub">
            Two units with machines from France, Germany, Japan, UK &amp; Taiwan — the largest CNC fleet in South India.
          </p>
        </div>
      </section>

      {/* ── FILTER BAR ── */}
      <div className="machines-filter-bar">
        <div className="container">
          <div className="filter-bar">
            {[
              { l: 'All Machines', v: 0 },
              { l: 'Unit II — Heavy Load First', v: 2 },
              { l: 'Unit I — VMC & CNC', v: 1 },
            ].map(u => (
              <button
                key={u.v}
                className={`filter-btn ${activeUnit === u.v ? 'filter-btn--active' : ''}`}
                onClick={() => { setActiveUnit(u.v); setSelectedId(null); }}
              >
                {u.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <section className="section machines-main" ref={gridRef}>
        <div className="container">
          <div className="machines-layout">

            {/* LEFT: Cards */}
            <div className="machines-list stagger">
              {filtered.map((m) => (
                <div key={m.id} className="reveal">
                  <MachineCard
                    machine={m}
                    isActive={selectedId === m.id}
                    onClick={() => handleCardClick(m.id)}
                    accent={ACCENTS[m.id - 1] || '#4F46E5'}
                    mobileDetail={true}
                  />
                </div>
              ))}
            </div>

            {/* RIGHT: Detail panel (desktop only) */}
            <div className="machines-detail-col">
              <div className="machines-detail-sticky reveal from-right">
                <MachineDetailPanel machine={selectedMachine} accent={selectedAccent} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INSPECTION FACILITIES — properly sized cards ── */}
      <section className="inspection-section section">
        <div className="container">
         
          <h2 className="section-title">Inspection Facilities</h2>
          <div className="line-ind" />
          <p className="section-sub" style={{ marginBottom: '2.5rem' }}>
            State-of-the-art measurement equipment for dimensional verification of all components.
            Uni-master (TESA) range 100–3500mm, Tubular Micrometer 1000–5000mm.
          </p>
          <div className="inspection-grid">
            {INSPECTION.map((item, i) => (
              <div key={i} className="inspection-item" style={{ '--ins-clr': item.clr }}>
                <div className="inspection-item__img-wrap">
                  <img
                    src={item.img}
                    alt={item.label}
                    className="inspection-item__img"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="inspection-item__info">
                  <strong>{item.label}</strong>
                  <span>{item.spec}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── KEY SPECS HIGHLIGHTS ── */}
      <section className="machines-highlights-section">
        <div className="container">
          <div className="machines-highlights-grid">
            {[
              { label: 'Berthiez CNC VTL', spec: 'Diameter Ø 8200 mm', color: '#4F46E5' },
              { label: 'CNC Gantry Floor Boring', spec: '16000 × 4000 × 3500 mm', color: '#E8192C' },
              { label: 'VMC Table with 4th Axis', spec: '2000 × 1000 × 1000 mm', color: '#10B981' },
            ].map((h, i) => (
              <div key={i} className="machines-highlight-card" style={{ '--hl': h.color }}>
                <div className="machines-highlight-card__icon">⚙</div>
                <div>
                  <strong>{h.label}</strong>
                  <span>{h.spec}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}